/**
 * ============================================================
 * HOBBLIVISION OCR SERVICE
 * ============================================================
 *
 * OCR V2 uses the regions selected by the text-localization pass.
 * It does NOT assume top/middle/bottom and it does NOT OCR every
 * preprocessing variant unless the first pass needs help.
 *
 * Typical path per region:
 *   original (or rotated original)
 *       ↓ if weak
 *   enhanced
 *       ↓ if still weak
 *   grayscale
 *
 * This keeps OCR work bounded while retaining fallbacks for difficult
 * labels, logos, vertical text and noisy card faces.
 * ============================================================
 */

import { createWorker } from "tesseract.js";

const OCR_VERSION = "2.0";

const CONFIG = {
    language: "eng",
    minimumReportConfidence: 20,
    goodConfidence: 45,
    minimumUsefulCharacters: 4,
    maxRegions: 4,
    maxVariantsPerRegion: 2,
    variants: ["original", "rotatedOriginal", "enhanced", "grayscale"],
};

export async function runOCR(ocrPreprocessed) {
    const started = performance.now();

    if (!ocrPreprocessed?.regions) {
        return createEmptyResult();
    }

    const entries = Object.entries(ocrPreprocessed.regions)
        .slice(0, CONFIG.maxRegions);

    if (!entries.length) {
        return createEmptyResult();
    }

    console.log("HobbliVision OCR: starting targeted OCR...", {
        regionCount: entries.length,
    });

    const worker = await createWorker(CONFIG.language);
    const results = {};

    try {
        for (const [regionName, region] of entries) {
            results[regionName] = {};

            const orderedVariants = chooseVariants(region);
            let successfulAttempts = 0;
            let bestConfidence = 0;
            let bestText = "";

            for (const variantName of orderedVariants) {
                const variant = region?.[variantName];
                if (!variant) continue;

                const imageSource = getImageSource(variant);
                if (!imageSource) continue;

                const variantStarted = performance.now();
                try {
                    const { data } = await worker.recognize(imageSource);
                    const text = cleanOCRText(data?.text);
                    const confidence = Number((data?.confidence ?? 0).toFixed(2));
                    const processingMs = Number((performance.now() - variantStarted).toFixed(0));
                    const useful = isUseful(text, confidence);

                    results[regionName][variantName] = {
                        text,
                        confidence,
                        useful,
                        width: variant.width || null,
                        height: variant.height || null,
                        processingMs,
                        regionConfidence: region.confidence || 0,
                        orientation: region.orientation || 0,
                        type: region.type || "text",
                    };

                    successfulAttempts++;
                    if (confidence > bestConfidence || (!bestText && text)) {
                        bestConfidence = confidence;
                        bestText = text;
                    }

                    console.log(
                        `HobbliVision OCR: ${regionName}/${variantName} complete.`,
                        { confidence, text, processingMs, useful }
                    );

                    if (useful || successfulAttempts >= CONFIG.maxVariantsPerRegion) {
                        break;
                    }
                } catch (error) {
                    results[regionName][variantName] = {
                        text: "",
                        confidence: 0,
                        useful: false,
                        error: error.message,
                    };
                }
            }

            results[regionName]._meta = {
                confidence: region.confidence || 0,
                textness: region.textness || 0,
                orientation: region.orientation || 0,
                type: region.type || "text",
                attemptedVariants: Object.keys(results[regionName]).filter(k => k !== "_meta"),
                bestConfidence,
                bestText,
            };
        }

        const processingMs = Number((performance.now() - started).toFixed(0));
        return {
            version: OCR_VERSION,
            language: CONFIG.language,
            regions: results,
            summary: summarizeOCRResults(results),
            processingMs,
            createdAt: new Date().toISOString(),
        };
    } finally {
        await worker.terminate();
    }
}

function chooseVariants(region) {
    const orientation = Number(region?.orientation || 0);
    if (orientation === 90 || orientation === 270) {
        return ["rotatedOriginal", "original", "enhanced", "grayscale"];
    }
    return ["original", "enhanced", "grayscale"];
}

function isUseful(text, confidence) {
    const alphanumeric = (text || "").replace(/[^A-Za-z0-9]/g, "");
    return (
        confidence >= CONFIG.goodConfidence &&
        alphanumeric.length >= CONFIG.minimumUsefulCharacters
    );
}

function getImageSource(variant) {
    if (!variant) return null;
    if (variant.canvas) return variant.canvas;
    if (variant.imageUrl) return variant.imageUrl;
    return null;
}

function cleanOCRText(text) {
    if (!text) return "";
    return String(text)
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+/g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

function summarizeOCRResults(results) {
    let variantCount = 0;
    let successfulVariants = 0;
    let bestResult = null;
    let bestConfidence = 0;

    Object.entries(results).forEach(([regionName, variants]) => {
        Object.entries(variants || {}).forEach(([variantName, result]) => {
            if (variantName === "_meta" || !result) return;
            variantCount++;
            if (result.text) successfulVariants++;
            if (result.confidence > bestConfidence) {
                bestConfidence = result.confidence;
                bestResult = {
                    region: regionName,
                    variant: variantName,
                    confidence: result.confidence,
                    text: result.text,
                };
            }
        });
    });

    return {
        regionCount: Object.keys(results).length,
        variantCount,
        successfulVariants,
        bestConfidence,
        bestResult,
    };
}

function createEmptyResult() {
    return {
        version: OCR_VERSION,
        language: CONFIG.language,
        regions: {},
        summary: {
            regionCount: 0,
            variantCount: 0,
            successfulVariants: 0,
            bestConfidence: 0,
            bestResult: null,
        },
        processingMs: 0,
        createdAt: new Date().toISOString(),
    };
}
